import { useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { type AvatarData } from '@/api';
import { useViewport } from '@/hooks';
import { ResetIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { AnimatedAvatar } from '@/shared/features/user-avatar';
import { Button, DialogHeader, IconButton, ResponsiveDialog } from '@/ui_v3';
import { AvatarPartSelector } from './components/AvatarPartSelector';
import { AvatarPreviewPanel } from './components/AvatarPreviewPanel';
import { MyPointsChip } from './components/MyPointsChip';
import { RandomAvatarButton } from './components/RandomAvatarButton';
import { type AvatarSaveStrategy } from './hooks/types';
import { type AvatarEventSource } from './events.quiz-profile-avatar';
import { type AvatarDecorateForm, AvatarDecorateSchema } from './schema';
interface AvatarDecorateModalProps {
  /** 현재 아바타 (없으면 기본 파츠). */
  initialAvatar?: AvatarData | null;
  onClose: () => void;
  /** 저장 전략 주입: /profile/edit=프로필 DB, play=게임 SET_AVATAR. */
  onSave: AvatarSaveStrategy['save'];
  /** 모달 진입 위치. 저장·배너 클릭 이벤트와 동일한 값으로 연결한다. */
  source: AvatarEventSource;
  isSaving?: boolean;
  insufficientPointHelpHref?: string;
}
const toFormValue = (avatar?: AvatarData | null): AvatarDecorateForm => ({
  avatar: {
    skinId: avatar?.skinId ?? 1,
    faceId: avatar?.faceId ?? 1,
    hairId: avatar?.hairId ?? 1,
    clothesId: avatar?.clothesId ?? 1,
    accessoryIds: avatar?.accessoryIds as Record<string, number> ?? {}
  }
});
function RestoreSavedAvatarIconButton({
  onReset,
  disabled,
  ariaLabel
}: {
  onReset: () => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  return <IconButton variant="outlined" intent="neutral" size="md" rounded="default" disabled={disabled} aria-label={ariaLabel} onClick={onReset} data-sentry-element="IconButton" data-sentry-component="RestoreSavedAvatarIconButton" data-sentry-source-file="AvatarDecorateModal.tsx">
      <ResetIcon size="sm" color={disabled ? ICON_COLOR.disabled : ICON_COLOR.neutral} data-sentry-element="ResetIcon" data-sentry-source-file="AvatarDecorateModal.tsx" />
    </IconButton>;
}
function MobileAvatarPreview() {
  const {
    watch
  } = useFormContext<AvatarDecorateForm>();
  const avatar = watch('avatar');
  return <div className="flex h-[100px] w-full shrink-0 items-start justify-center pt-none" data-sentry-component="MobileAvatarPreview" data-sentry-source-file="AvatarDecorateModal.tsx">
      <div className="flex h-full flex-1 items-center justify-center px-md">
        <div className="flex h-[100px] w-[86px] shrink-0 items-center justify-center overflow-hidden">
          <AnimatedAvatar avatarData={avatar} behavior="stand" className="origin-center scale-[1.5]" data-sentry-element="AnimatedAvatar" data-sentry-source-file="AvatarDecorateModal.tsx" />
        </div>
      </div>
    </div>;
}
export const AvatarDecorateModal = ({
  initialAvatar,
  onClose,
  onSave,
  source,
  isSaving,
  insufficientPointHelpHref
}: AvatarDecorateModalProps) => {
  const {
    t
  } = useTranslation('common');
  const {
    isAuthenticated
  } = useUser();
  const {
    isMobile,
    isTabletSm
  } = useViewport();
  const isMobileLayout = isMobile || isTabletSm;
  const form = useForm<AvatarDecorateForm>({
    resolver: zodResolver(AvatarDecorateSchema),
    defaultValues: toFormValue(initialAvatar)
  });

  // 모달은 열릴 때마다 새로 mount 되므로 실제 노출 1회만 발화한다.
  useEffect(() => {
    trackEvent('quiz_profile_avatar_dialog_view', {
      source,
      login_state: isAuthenticated ? 'authenticated' : 'guest'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSave = form.handleSubmit(async ({
    avatar
  }) => {
    try {
      await onSave(avatar);
      onClose();
    } catch (error) {
      logger.captureError(error);
    }
  });
  const isDirty = form.formState.isDirty;
  const handleResetToSaved = () => {
    form.reset(toFormValue(initialAvatar));
  };
  if (isMobileLayout) {
    return <ResponsiveDialog size="lg" className="h-[calc(100dvh-60px)] !max-h-[calc(100dvh-60px)] rounded-t-lg border-0" onClose={onClose}>
        <FormProvider {...form}>
          <DialogHeader title={t('avatarDecorate.title')} hasCloseButton onClose={onClose} className="px-md pb-xs pt-md" />
          <div className="flex shrink-0 items-center justify-between px-md pb-none">
            <div className="flex items-center gap-xs">
              <RestoreSavedAvatarIconButton onReset={handleResetToSaved} disabled={!isDirty} ariaLabel={t('avatarDecorate.restoreSavedAriaLabel')} />
              <RandomAvatarButton iconOnly />
            </div>
            {/* 데스크탑은 우측 프리뷰 패널에 있는 잔액 칩. 모바일은 그 패널을 쓰지 않아 여기 둔다. */}
            <MyPointsChip />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <MobileAvatarPreview />
            <div className="min-h-0 flex-1 overflow-hidden">
              <AvatarPartSelector insufficientPointHelpHref={insufficientPointHelpHref} source={source} />
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-center gap-xs bg-default px-md pb-md pt-xs">
            <Button variant="contained" intent="strong" size="md" className="h-[40px] flex-1" disabled={!isDirty || isSaving} onClick={handleSave}>
              {t('avatarDecorate.save')}
            </Button>
          </div>
        </FormProvider>
      </ResponsiveDialog>;
  }
  return <ResponsiveDialog size="xl" className="flex h-[min(640px,calc(100dvh-80px))] !max-h-[640px] mobile-lg:!w-[calc(100vw-80px)] mobile-lg:!max-w-[960px]" onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="AvatarDecorateModal" data-sentry-source-file="AvatarDecorateModal.tsx">
      <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-source-file="AvatarDecorateModal.tsx">
        <DialogHeader title={t('avatarDecorate.title')} hasCloseButton onClose={onClose} className="border-b border-assistive" data-sentry-element="DialogHeader" data-sentry-source-file="AvatarDecorateModal.tsx" />
        <div className="flex min-h-0 flex-1 flex-col mobile-lg:flex-row">
          {/*
            태블릿(모달 최대폭 < 960px)에서는 좌측 그룹이 먼저 줄어야 우측 프리뷰(300px)가
            잘리지 않는다. flex-none 이면 660px 를 고집해 우측이 깎였다.
           */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden mobile-lg:w-[660px] mobile-lg:flex-initial">
            <AvatarPartSelector insufficientPointHelpHref={insufficientPointHelpHref} source={source} data-sentry-element="AvatarPartSelector" data-sentry-source-file="AvatarDecorateModal.tsx" />
          </div>
          {/*
            Figma(1333:56263): 프리뷰 패널은 min-w 250 / max-w 300 으로 같이 줄어든다.
            shrink-0 로 300px 를 고정하면 축소분이 전부 아이템 그리드로 몰려 1열까지 찌그러진다.
           */}
          <div className="flex shrink-0 flex-col border-assistive mobile-lg:w-[300px] mobile-lg:min-w-[250px] mobile-lg:shrink mobile-lg:border-l">
            <AvatarPreviewPanel onSave={handleSave} isSaving={isSaving} onResetToSaved={handleResetToSaved} source={source} data-sentry-element="AvatarPreviewPanel" data-sentry-source-file="AvatarDecorateModal.tsx" />
          </div>
        </div>
      </FormProvider>
    </ResponsiveDialog>;
};